import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, Heart, Gem, TrendingUp, Clock, Sparkles, Users, ArrowRight, CheckCircle } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';

function parsePriceRupee(priceStr) {
  const m = String(priceStr).match(/₹([\d,]+)/);
  return m ? parseInt(m[1].replace(/,/g, ''), 10) : 0;
}

/** Split "Mini Insight Session - ₹2,500" into session label + fee for aligned layouts */
function parseSessionAndFee(priceStr) {
  const s = String(priceStr);
  const idx = s.lastIndexOf(' - ');
  if (idx === -1) return { sessionType: s, fee: '' };
  return {
    sessionType: s.slice(0, idx).trim(),
    fee: s.slice(idx + 3).trim(),
  };
}

const services = [
  {
    name: 'Annual Kundali Reading',
    sanskrit: 'वार्षिक कुंडली विश्लेषण परामर्श',
    description: 'Year ahead from your chart: key themes, timing, and dasha highlights in one session.',
    icon: BookOpen,
    price: 'Mini Insight Session - ₹2,500',
    detailedDescription: 'Yearly Kundali read: transits, sensitive windows, and dasha/bhukti for the next twelve months, with practical remedies.',
    features: ['Annual themes & timing', 'Transits & sensitive periods', 'Dasha focus & remedies'],
    duration: '15 minutes',
    image: 'https://images.unsplash.com/photo-1729335511883-29eade10006b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx2ZWRpYyUyMGFzdHJvbG9neSUyMGJpcnRoJTIwY2hhcnR8ZW58MXx8fHwxNzY2MTIyMzkzfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral'
  },
  {
    name: 'Career & Finance Reading',
    sanskrit: 'करियर और वित्त',
    description: 'Career path, income, and favourable timing for work and money choices.',
    icon: TrendingUp,
    price: 'Focussed Insight Session - ₹5,000',
    detailedDescription: 'Planetary read on career direction, earnings, and changes—aligned to your horoscope.',
    features: ['Career & income outlook', 'Job or shift timing', 'Money & decisions'],
    duration: '30 minutes',
    image: 'https://images.unsplash.com/photo-1630344745991-fb948c5bf9d1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHN1Y2Nlc3MlMjBncm93dGh8ZW58MXx8fHwxNzY2MDM2MjE2fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral'
  },
  {
    name: 'Personality Strengths Reading',
    sanskrit: 'व्यक्तित्व विश्लेषण',
    description: 'Strengths, blind spots, and emotional patterns—clear and practical.',
    icon: Users,
    price: 'Mini Insight Session - ₹2,500',
    detailedDescription: 'Talents and growth areas from the chart, with grounded guidance.',
    features: ['Strengths & challenges', 'Patterns & habits', 'Growth focus'],
    duration: '15 minutes',
    image: 'https://images.unsplash.com/photo-1595243463493-55235da19aec?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxndXJ1JTIwY29uc3VsdGF0aW9uJTIwd2lzZG9tfGVufDF8fHx8MTc2NjEyMjM5NXww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral'
  },
  {
    name: 'Marriage & Relationship Reading',
    sanskrit: 'विवाह और रिश्ते',
    description: 'Love, compatibility, and marriage timing—with calm, clear guidance.',
    icon: Heart,
    price: 'Comprehensive Reading - ₹7,000',
    detailedDescription: 'Compatibility and relationship harmony through chart-based Vedic insight.',
    features: ['Match & dynamics', 'Timing outlook', 'Harmony & remedies'],
    duration: '45 minutes',
    image: 'https://images.unsplash.com/photo-1631609473077-67318e84b31c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb3VwbGUlMjBob2xkaW5nJTIwaGFuZHMlMjBsb3ZlfGVufDF8fHx8MTc2NjEyMjM5M3ww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral'
  },
  {
    name: 'Child Strengths Reading',
    sanskrit: 'बाल शक्ति विश्लेषण',
    description: 'Your child’s talents and needs—support for growth, learning, and confidence.',
    icon: Sparkles,
    price: 'Focussed Insight Session - ₹5,000',
    detailedDescription: 'Strengths and developmental pointers from the child’s chart.',
    features: ['Talents & temperament', 'Learning & habits', 'Guidance for parents'],
    duration: '30 minutes',
    image: 'https://images.unsplash.com/photo-1603983616619-faf118d6c374?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtZWRpdGF0aW9uJTIwc3Bpcml0dWFsJTIwcHJhY3RpY2V8ZW58MXx8fHwxNzY2MTIyMzk1fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral'
  },
  {
    name: 'Name & Numerology Reading',
    sanskrit: 'नाम ज्योतिष',
    description: 'Name and chart alignment—sound, spelling, and balance in a mini session.',
    icon: BookOpen,
    price: 'Mini Insight Session - ₹2,500',
    detailedDescription: 'Name vibration check and practical naming pointers from astrology.',
    features: ['Name & chart fit', 'Letters & sounds', 'Balancing suggestions'],
    duration: '15 minutes',
    image: 'https://images.unsplash.com/photo-1572442568216-e4a31af30b69?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBjYWxlbmRhciUyMHNwaXJpdHVhbHxlbnwxfHx8fDE3NjYxMjIzOTN8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral'
  },
  {
    name: 'Instant Insight Reading',
    sanskrit: 'तत्काल परामर्श',
    description: 'Urgent questions—quick chart-based guidance when you cannot wait.',
    icon: Clock,
    price: 'Mini Insight Session - ₹2,500',
    detailedDescription: 'Fast answers and next steps for pressing decisions.',
    features: ['Priority clarity', 'Immediate guidance', 'Simple remedies'],
    duration: '15 minutes',
    image: 'https://images.unsplash.com/photo-1559891272-8a206fbc5c0e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxnb2xkZW4lMjBjbG9jayUyMHRpbWV8ZW58MXx8fHwxNzY2MTIyMzk1fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral'
  },
  {
    name: 'Gemstone Selection Reading',
    sanskrit: 'रत्न परामर्श',
    description: 'Birth-chart–only gemstone advice—no generic shopping lists.',
    icon: Gem,
    price: 'Focussed Insight Session - ₹5,000',
    detailedDescription: 'Selection, quality, and wearing guidance suited to your planets.',
    features: ['Right stone & metal', 'Wear & care', 'Activation basics'],
    duration: '30 minutes',
    image: 'https://images.unsplash.com/photo-1603561117745-b1fc134ed79d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcmVjaW91cyUyMGdlbXN0b25lcyUyMGNyeXN0YWxzfGVufDF8fHx8MTc2NjEyMjM5NHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral'
  },
  {
    name: 'Property & Assets Reading',
    sanskrit: 'संपत्ति विश्लेषण',
    description: 'Home, property, and stability—timing and outlook from your chart.',
    icon: Calendar,
    price: 'Comprehensive Reading - ₹7,000',
    detailedDescription: 'Assets and major purchase decisions: timing and planetary support.',
    features: ['Buy/sell timing', 'Investment read', 'Security & wealth themes'],
    duration: '45 minutes',
    image: 'https://images.unsplash.com/photo-1572442568216-e4a31af30b69?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBjYWxlbmRhciUyMHNwaXJpdHVhbHxlbnwxfHx8fDE3NjYxMjIzOTN8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral'
  },
  {
    name: 'Complete Life Chart Reading',
    sanskrit: 'संपूर्ण कुंडली विश्लेषण',
    description: 'Full birth chart: life areas, dasha story, and remedies in one deep session.',
    icon: Users,
    price: 'Deep Dive Reading - ₹9,000',
    detailedDescription: 'End-to-end Kundli: houses, dasha, predictions, and holistic remedies.',
    features: ['Life overview & houses', 'Dasha & timing', 'Holistic remedies'],
    duration: '60 minutes',
    image: 'https://images.unsplash.com/photo-1729335511883-29eade10006b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx2ZWRpYyUyMGFzdHJvbG9neSUyMGJpcnRoJTIwY2hhcnR8ZW58MXx8fHwxNzY2MTIyMzkzfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral'
  }
].sort((a, b) => {
  const d = parsePriceRupee(a.price) - parsePriceRupee(b.price);
  return d !== 0 ? d : a.name.localeCompare(b.name);
});

export function ZodiacGrid() {
  return (
    <section className="relative py-16 sm:py-24 bg-gradient-to-b from-slate-100 via-white to-white border-y-4 border-goldSoft/40 shadow-[inset_0_1px_0_0_rgba(212,168,95,0.25)]">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-goldSoft/80 to-transparent" aria-hidden />
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-16">
          <p className="mb-3 text-sm sm:text-base font-bold uppercase tracking-[0.2em] text-goldSoft">
            What we offer
          </p>
          <h2 id="services" className="mb-5 text-indigoDeep text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight [text-shadow:0_2px_24px_rgba(26,31,54,0.12)]">
            Our Services
          </h2>
          <div className="mx-auto mb-6 h-1.5 w-24 sm:w-32 rounded-full bg-gradient-to-r from-goldSoft via-amber-400 to-goldSoft shadow-md shadow-goldSoft/30" aria-hidden />
          <p className="text-gray-800 max-w-3xl mx-auto text-lg sm:text-xl font-semibold leading-relaxed">
            Explore our comprehensive range of authentic Vedic astrology services
            designed to bring clarity, guidance, and prosperity to your life.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-7 lg:gap-8">
          {services.map((service) => {
            const Icon = service.icon;
            const { sessionType, fee } = parseSessionAndFee(service.price);
            return (
              <div
                key={service.name}
                className="group h-[540px] sm:h-[560px] lg:h-[580px] [perspective:1000px]"
              >
                <div className="relative h-full w-full rounded-xl shadow-xl shadow-indigoDeep/20 ring-2 ring-goldSoft/50 transition-all duration-700 [transform-style:preserve-3d] group-hover:shadow-2xl group-hover:ring-goldSoft group-hover:[transform:rotateY(180deg)]">
                  {/* Front of card */}
                  <div className="absolute inset-0 h-full w-full rounded-xl [backface-visibility:hidden] overflow-hidden">
                    <div className="relative h-full">
                      {/* Background Image with Overlay */}
                      <div className="absolute inset-0">
                        <ImageWithFallback 
                          src={service.image} 
                          alt={service.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/45" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/25" />
                      </div>
                      
                      {/* Content */}
                      <div className="relative z-10 h-full border-[3px] border-goldSoft/70 rounded-xl p-5 sm:p-6 flex flex-col ring-1 ring-white/10 overflow-hidden">
                        <div className="mb-3 inline-flex p-3.5 bg-goldSoft/55 backdrop-blur-md rounded-full w-fit ring-2 ring-white/50 shadow-lg">
                          <Icon className="w-10 h-10 text-indigoDeep drop-shadow-md" strokeWidth={2.25} />
                        </div>
                        <h3 className="mb-2 min-h-[3.25rem] sm:min-h-[3.75rem] text-white text-xl sm:text-2xl font-black leading-snug line-clamp-2 drop-shadow-lg [text-shadow:0_2px_12px_rgba(0,0,0,0.85)]">{service.name}</h3>
                        <p className="text-sm mb-3 text-amber-200 font-extrabold tracking-wide drop-shadow-lg [text-shadow:0_1px_6px_rgba(0,0,0,0.85)] line-clamp-1">{service.sanskrit}</p>
                        <dl className="mb-3 rounded-lg border border-white/25 bg-black/35 px-3 py-2.5 backdrop-blur-sm text-xs sm:text-sm shadow-inner grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 items-baseline">
                          <dt className="font-black uppercase tracking-wider text-amber-200/95 [text-shadow:0_1px_4px_rgba(0,0,0,0.9)]">Session</dt>
                          <dd className="font-bold text-white text-right sm:text-left min-w-0 break-words [text-shadow:0_1px_4px_rgba(0,0,0,0.85)]">{sessionType}</dd>
                          <dt className="font-black uppercase tracking-wider text-amber-200/95 [text-shadow:0_1px_4px_rgba(0,0,0,0.9)]">Fee</dt>
                          <dd className="font-black text-goldSoft tabular-nums text-right sm:text-left [text-shadow:0_1px_4px_rgba(0,0,0,0.85)]">{fee}</dd>
                          <dt className="font-black uppercase tracking-wider text-amber-200/95 [text-shadow:0_1px_4px_rgba(0,0,0,0.9)]">Time</dt>
                          <dd className="font-bold text-white flex items-center justify-end sm:justify-start gap-1.5 min-w-0 [text-shadow:0_1px_4px_rgba(0,0,0,0.85)]">
                            <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 text-amber-200" strokeWidth={2.5} aria-hidden />
                            <span className="tabular-nums">{service.duration}</span>
                          </dd>
                        </dl>
                        <p className="text-sm text-white mb-3 flex-1 min-h-0 leading-snug font-semibold [text-shadow:0_2px_8px_rgba(0,0,0,0.95),0_0_1px_rgba(0,0,0,1)]">{service.description}</p>
                        <div className="mt-auto pt-4 border-t-[3px] border-goldSoft/60">
                          <div className="flex items-center justify-center text-sm text-goldSoft font-black uppercase tracking-wide drop-shadow">
                            <span>Hover for details</span>
                            <ArrowRight className="w-5 h-5 ml-2 stroke-[3] group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Back of card */}
                  <div className="absolute inset-0 h-full w-full rounded-xl [backface-visibility:hidden] [transform:rotateY(180deg)] overflow-hidden ring-2 ring-goldSoft/50">
                    <div className="h-full min-h-0 bg-gradient-to-br from-indigoDeep to-indigoDark border-[3px] border-goldSoft/60 rounded-xl p-5 sm:p-6 flex flex-col text-white shadow-inner overflow-hidden">
                      <h3 className="mb-3 text-white text-xl sm:text-2xl font-black leading-snug line-clamp-2">{service.name}</h3>
                      <dl className="mb-3 rounded-lg border border-white/20 bg-white/5 px-3 py-2.5 text-sm grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 items-baseline">
                        <dt className="font-black uppercase tracking-wider text-amber-300">Session</dt>
                        <dd className="font-bold text-goldSoft text-right sm:text-left min-w-0 break-words">{sessionType}</dd>
                        <dt className="font-black uppercase tracking-wider text-amber-300">Fee</dt>
                        <dd className="font-black text-white tabular-nums text-right sm:text-left">{fee}</dd>
                        <dt className="font-black uppercase tracking-wider text-amber-300">Time</dt>
                        <dd className="font-bold text-amber-200/95 flex items-center justify-end sm:justify-start gap-2">
                          <Clock className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
                          <span className="tabular-nums">{service.duration}</span>
                        </dd>
                      </dl>
                      <div className="flex-1 flex flex-col gap-2.5 min-h-0">
                        <p className="text-sm text-gray-100 leading-snug font-semibold">{service.detailedDescription}</p>
                        <div className="min-h-0">
                          <p className="text-[10px] uppercase tracking-[0.15em] mb-1.5 text-amber-300 font-black">What&apos;s Included</p>
                          <ul className="space-y-1.5">
                            {service.features.map((feature, index) => (
                              <li key={index} className="flex items-start gap-1.5 text-sm leading-snug">
                                <CheckCircle className="w-4 h-4 text-goldSoft flex-shrink-0 mt-0.5 stroke-[2.5]" />
                                <span className="text-white/95 font-semibold">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t-[3px] border-goldSoft/50 relative z-20 [transform:translateZ(1px)]">
                        <Link 
                          to="/booking"
                          className="block w-full px-6 py-3.5 bg-goldSoft text-indigoDeep hover:bg-goldHover rounded-full transition-all shadow-lg font-black text-center text-base ring-2 ring-white/20"
                        >
                          Book Now
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const Services = ZodiacGrid;

export default Services;